import ApiClient from '@core/api/client'
import ErrorPage from '@core/components/ErrorPage'
import LoadingScreen from '@core/components/LoadingScreen'
import { lazy, Suspense } from 'react'
import { createBrowserRouter, Navigate } from 'react-router-dom'
import {
  RedirectAdvocateRedemption,
  RedirectAdvocateRedemptionPast,
  RedirectR,
  RedirectR0,
  RedirectRefer,
} from './redirects'

const PhotoProductsApp = lazy(() => import('@features/photo-products'))
const DPFLandingPageWrapper = lazy(
  () => import('@features/photo-products/pages/landing/dpf/DPFLandingPageWrapper')
)
const LoopApp = lazy(() => import('@features/loop'))
const ReferralApp = lazy(() => import('@features/referral'))
const UtilsApp = lazy(() => import('@features/utils'))
const EgiftsApp = lazy(() => import('@features/egifts'))

const api = new ApiClient({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  useFaPhotoProxy: import.meta.env.VITE_USE_FA_PHOTO_PROXY === 'true',
})

export const createAppRouter = ({
  supportEmail,
  isPrintifyPreviewLogsActive,
  familyAlbumDownloadDeepLink,
  familyAlbumStoreDeepLink,
  familyAlbumAutoLoginUrl,
}: {
  supportEmail: string
  isPrintifyPreviewLogsActive: boolean
  familyAlbumAutologinDeeplink: string
  familyAlbumDownloadDeepLink: string
  familyAlbumStoreDeepLink: string
  familyAlbumAutoLoginUrl: string
  explanationVideoUrl: string
}) => {
  return createBrowserRouter([
    // Paths for lazy loaded feature entry points
    {
      path: '/photo-products/dpf-landing-jp',
      element: (
        <Suspense fallback={<LoadingScreen />}>
          <DPFLandingPageWrapper variant="JP" />
        </Suspense>
      ),
    },
    {
      path: '/photo-products/dpf-landing-us',
      element: (
        <Suspense fallback={<LoadingScreen />}>
          <DPFLandingPageWrapper variant="US" />
        </Suspense>
      ),
    },
    {
      path: '/photo-products/*',
      element: (
        <Suspense fallback={<LoadingScreen />}>
          <PhotoProductsApp
            supportEmail={supportEmail}
            isPrintifyPreviewLogsActive={isPrintifyPreviewLogsActive}
          />
        </Suspense>
      ),
    },
    {
      path: '/loop/*',
      element: (
        <Suspense fallback={<LoadingScreen />}>
          <LoopApp familyAlbumAutoLoginUrl={familyAlbumAutoLoginUrl} />
        </Suspense>
      ),
    },
    {
      path: 'referral/*',
      element: (
        <Suspense fallback={<LoadingScreen />}>
          <ReferralApp
            supportEmail={supportEmail}
            familyAlbumDownloadDeepLink={familyAlbumDownloadDeepLink}
            familyAlbumStoreDeepLink={familyAlbumStoreDeepLink}
            familyAlbumAutoLoginUrl={familyAlbumAutoLoginUrl}
            api={api}
          />
        </Suspense>
      ),
    },
    {
      path: '/utils/*',
      element: (
        <Suspense fallback={<LoadingScreen />}>
          <UtilsApp />
        </Suspense>
      ),
    },
    {
      path: '/egifts/*',
      element: (
        <Suspense fallback={<LoadingScreen />}>
          <EgiftsApp />
        </Suspense>
      ),
    },
    // Redirections for old routes
    {
      path: '/photo-gifts',
      element: <Navigate to="/photo-products" replace />,
    },
    {
      path: '/refer/:advocateReferralCode',
      element: <RedirectRefer />,
    },
    {
      path: '/advocate/:advocateReferralCode/redemptions/:redemptionId',
      element: <RedirectAdvocateRedemption />,
    },
    {
      path: '/advocate/:advocateReferralCode/redemptions/:redemptionId/past',
      element: <RedirectAdvocateRedemptionPast />,
    },
    {
      path: '/redeem',
      element: <Navigate to="/referral/redeem" replace />,
    },
    {
      path: '/r/:advocateReferralCode',
      element: <RedirectR />,
    },
    {
      path: '/r0/:advocateReferralCode',
      element: <RedirectR0 />,
    },
    {
      path: '/photo-products-tou',
      element: <Navigate to="/photo-products/tou" replace />,
    },
    {
      path: '/photo-products-supplemental-tou',
      element: <Navigate to="/photo-products/supplemental-tou" replace />,
    },
    {
      path: '/pod-modal-milestone-1-month',
      element: <Navigate to="/photo-products/buy-now?headerType=milestone-1-month" replace />,
    },
    {
      path: '/pod-modal-milestone-3-month',
      element: <Navigate to="/photo-products/buy-now?headerType=milestone-3-month" replace />,
    },
    {
      path: '/pod-modal-milestone-6-month',
      element: <Navigate to="/photo-products/buy-now?headerType=milestone-6-month" replace />,
    },
    {
      path: '/pod-modal-milestone-9-month',
      element: <Navigate to="/photo-products/buy-now?headerType=milestone-9-month" replace />,
    },
    {
      path: '/pod-lp-child-birthday',
      element: <Navigate to="/photo-products/buy-now?headerType=child-birthday" replace />,
    },
    {
      path: '/pod-lp-adult-birthday',
      element: <Navigate to="/photo-products/buy-now?headerType=adult-birthday" replace />,
    },
    {
      path: '*',
      element: <ErrorPage error={404} message="Page Not Found" />,
    },
  ])
}
